import React from 'react';
import {Chip, Divider, IconButton, Link as MuiLink, Paper, Stack, Tooltip, Typography} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TabLoading from './TabLoading';
import TabEmpty from './TabEmpty';
import {useSession} from '../../../firebase/UserProvider';
import {highlightEntityInSnippet} from '../utils/highlightEntityInSnippet';
import {usePagination} from './usePagination';
import TabPagination from './TabPagination';

const SOURCES_API_BASE = (
    process.env.REACT_APP_SOURCES_API_BASE
    || process.env.REACT_APP_AWP_API_URL
    || 'https://awp.minsky.ai'
).replace(/\/$/, '');

/**
 * Build a deeplink into the report editor's source-file viewer for a data item.
 * Mirrors the URL shape used by the report editor itself, so the sources panel
 * and file viewer open automatically on the correct file.
 */
function buildViewerUrl(dataItemId, title, sourceType, reportId, mimeType) {
    if (!dataItemId || !reportId) return null;
    const fileType = resolveFileType(sourceType, mimeType);
    const params = new URLSearchParams({
        leftPanelTab: 'sources',
        sourcesView: 'preset',
        sourcesBranch: 'my',
        isSourceFileViewerOpen: 'true',
        sourceViewerReportId: reportId,
        sourceViewerFileId: String(dataItemId),
        sourceViewerFileName: title || '',
        sourceViewerFileType: fileType,
        sourceViewerViewKey: 'preset:my',
    });
    return `/products/${reportId}/edit?${params.toString()}`;
}

/**
 * Raw-stream fallback for items not linked to any report.
 */
function buildRawStreamUrl(dataItemId, userId) {
    if (dataItemId == null || !userId) return null;
    return `${SOURCES_API_BASE}/api/data-items/${encodeURIComponent(dataItemId)}/raw/stream`
        + `?userId=${encodeURIComponent(userId)}&disposition=inline`;
}

function resolveFileType(sourceType, mimeType) {
    const t = (sourceType || mimeType || '').toLowerCase();
    if (t === 'pdf' || t === 'application/pdf') return 'pdf';
    if (t === 'audio') return 'audio';
    if (t === 'video') return 'video';
    if (t === 'osint' || t === 'html' || t === 'text/html') return 'html';
    return 'document';
}

function typeLabel(item) {
    const raw = item.sourceType || item.mimeType || '';
    if (!raw) return null;
    return String(raw).toUpperCase();
}

/**
 * Files tab — displays the user's data-library files where the entity was detected.
 * Each card links into the report editor's source-file viewer (via the first associated
 * report), falling back to the raw-stream URL when no report link is available.
 */
const DataItemsTab = ({dataItems = [], loading, emptyMessage, entityHighlightContext}) => {
    const {user} = useSession() || {};
    const userId = user?.uid || null;
    const {page, setPage, pageItems, totalPages, hasPagination} = usePagination(dataItems);

    if (loading) return <TabLoading/>;
    if (!dataItems.length) return <TabEmpty message={emptyMessage || 'No data items mention this entity yet.'}/>;

    return (
        <Stack>
        <Stack spacing={2} sx={{bgcolor: 'grey.400'}}>
            {pageItems.map((item) => {
                const itemId = item.dataItemId ?? item.id;
                const title = item.title || `Data item ${itemId}`;
                const label = typeLabel(item);
                const mentionCount = Number(item.mentionCount) || 0;
                const snippets = Array.isArray(item.snippets) ? item.snippets : [];
                const reports = Array.isArray(item.reports) ? item.reports : [];
                const firstReport = reports[0] || null;

                // Prefer viewer deeplink via first associated report; fall back to raw stream.
                const viewerLink = buildViewerUrl(itemId, title, item.sourceType, firstReport?.reportId, item.mimeType);
                const rawLink = buildRawStreamUrl(itemId, userId);
                const primaryLink = viewerLink || rawLink;
                const isViewerLink = !!viewerLink;

                return (
                    <Paper key={itemId} variant="outlined" sx={{borderRadius: 0, border: 'none'}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} p={2}>
                            <Stack spacing={1}>
                                {primaryLink ? (
                                    <MuiLink
                                        href={primaryLink}
                                        target={isViewerLink ? '_self' : '_blank'}
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        variant="subtitle2"
                                        sx={{fontWeight: 600}}
                                    >
                                        {title}
                                    </MuiLink>
                                ) : (
                                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>{title}</Typography>
                                )}
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                                    {!!label && (
                                        <Chip size="small" variant="outlined" label={`Type: ${label}`}/>
                                    )}
                                    {firstReport && (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={firstReport.reportTitle || firstReport.reportId}
                                            sx={{maxWidth: 220, fontSize: 11}}
                                        />
                                    )}
                                    {reports.length > 1 && (
                                        <Typography variant="caption" color="text.secondary">
                                            +{reports.length - 1} more report{reports.length > 2 ? 's' : ''}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                                <Chip
                                    label={`${mentionCount} mention${mentionCount === 1 ? '' : 's'}`}
                                    size="small"
                                    variant="outlined"
                                />
                                {!!primaryLink && (
                                    <Tooltip
                                        title={isViewerLink ? 'Open in source viewer' : 'Open full document'}
                                        placement="top"
                                    >
                                        <IconButton
                                            component="a"
                                            href={primaryLink}
                                            target={isViewerLink ? '_self' : '_blank'}
                                            rel="noopener noreferrer"
                                            size="small"
                                            aria-label={`Open ${title}`}
                                        >
                                            <OpenInNewIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                        {!!snippets.length && <Divider sx={{my: 1}}/>}
                        <Stack pb={1}>
                            {snippets.map((snippet, idx) => (
                                <Stack
                                    key={`${itemId}-snippet-${idx}`}
                                    direction="row"
                                    alignItems="flex-start"
                                    px={2}
                                    py={1}
                                >
                                    <Tooltip title={'Snippet from data item'} placement="top">
                                        <Typography variant="caption"
                                                    sx={{minWidth: 28, color: 'text.secondary', pt: 0.5}}>
                                            {`${idx + 1}.`}
                                        </Typography>
                                    </Tooltip>
                                    <Typography variant="body2" flex={1} component="div">
                                        {highlightEntityInSnippet(snippet, entityHighlightContext)}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Paper>
                );
            })}
        </Stack>
        <TabPagination
            hasPagination={hasPagination}
            page={page}
            totalPages={totalPages}
            totalItems={dataItems.length}
            onChange={setPage}
        />
        </Stack>
    );
};

export default DataItemsTab;
